import { createPortal } from 'react-dom';
import { protectedImageProps } from '../../../../shared/utils/imageProtection';
import { useEffect, useState } from 'react';

const SIDEBAR_WIDTH = 342;

/**
 * @param {string}   url          - 이미지 URL
 * @param {string}   [alt]        - 이미지 alt 텍스트
 * @param {Function} onClose      - 닫기 핸들러
 * @param {boolean}  [fullScreen] - true면 사이드바 오프셋 없이 전체화면 기준 중앙 (기본 false)
 * @param {number}   [zIndex]     - 오버레이 z-index (기본 9999)
 * @param {Function} [onPrev]      - 이전 이미지 핸들러
 * @param {Function} [onNext]      - 다음 이미지 핸들러
 * @param {boolean}  [large]       - true면 더 큰 감상용 모달 크기
 * @param {boolean}  [keepHeaderVisible] - true면 헤더 아래 영역만 덮음
 * @param {number}   [currentIndex] - 현재 이미지 번호(1부터)
 * @param {number}   [totalCount]   - 전체 이미지 수
 */
export default function ImageLightbox({
  url,
  alt,
  onClose,
  fullScreen = false,
  zIndex = 9999,
  onPrev,
  onNext,
  large = false,
  keepHeaderVisible = false,
  currentIndex,
  totalCount,
}) {
  const hasNavigation = Boolean(onPrev && onNext);
  const overlayTop = keepHeaderVisible ? 'var(--header-height)' : 0;
  const [isCompact, setIsCompact] = useState(false);
  const showCounter = Number.isFinite(currentIndex) && Number.isFinite(totalCount) && totalCount > 0;

  useEffect(() => {
    const mediaQuery = window.matchMedia('(max-width: 720px)');
    const updateCompact = () => setIsCompact(mediaQuery.matches);
    updateCompact();
    mediaQuery.addEventListener('change', updateCompact);
    return () => mediaQuery.removeEventListener('change', updateCompact);
  }, []);

  useEffect(() => {
    if (!url) return undefined;

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onClose?.();
      if (!hasNavigation) return;
      if (e.key === 'ArrowLeft') onPrev();
      if (e.key === 'ArrowRight') onNext();
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [url, onClose, onPrev, onNext, hasNavigation]);

  if (!url) return null;

  return createPortal(
    <div
      onClick={onClose}
      style={{
        position: 'fixed',
        top: overlayTop, bottom: 0,
        left: fullScreen ? 0 : SIDEBAR_WIDTH, right: 0,
        zIndex,
        background: large ? 'rgba(0, 0, 0, 0.78)' : 'rgba(0, 0, 0, 0.45)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          position: 'relative',
          width: large ? (isCompact ? '100%' : 'min(1120px, calc(100% - 96px))') : '720px',
          maxWidth: large ? '100%' : 'calc(100% - 48px)',
          height: large ? (isCompact ? '100%' : 'min(760px, calc(100% - 96px))') : '520px',
          maxHeight: large ? '100%' : '85vh',
          background: '#050505',
          borderRadius: large && isCompact ? 0 : '16px',
          overflow: 'hidden',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}
      >
        <button
          onClick={onClose}
          style={{
            position: 'absolute', top: large && isCompact ? 14 : 12, right: large && isCompact ? 14 : 12,
            width: large ? 44 : 36, height: large ? 44 : 36, borderRadius: '50%',
            background: 'rgba(0,0,0,0.55)', border: 'none',
            cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
            zIndex: 1,
          }}
        >
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
            <path d="M1 1l12 12M13 1L1 13" stroke="#fff" strokeWidth="2" strokeLinecap="round"/>
          </svg>
        </button>
        {hasNavigation && (
          <>
            <button
              onClick={onPrev}
              aria-label="이전 사진"
              style={{
                position: 'absolute', left: large && isCompact ? 12 : 14, top: '50%', transform: 'translateY(-50%)',
                width: 44, height: 44, borderRadius: '50%',
                background: 'rgba(0,0,0,0.5)', border: '1px solid rgba(255,255,255,0.18)',
                cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                zIndex: 1,
              }}
            >
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                <path d="M15 18l-6-6 6-6" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
            <button
              onClick={onNext}
              aria-label="다음 사진"
              style={{
                position: 'absolute', right: large && isCompact ? 12 : 14, top: '50%', transform: 'translateY(-50%)',
                width: 44, height: 44, borderRadius: '50%',
                background: 'rgba(0,0,0,0.5)', border: '1px solid rgba(255,255,255,0.18)',
                cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                zIndex: 1,
              }}
            >
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                <path d="M9 6l6 6-6 6" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
          </>
        )}
        <img
          src={url}
          alt={alt || '이미지'}
          {...protectedImageProps({ width: '100%', height: '100%', objectFit: 'contain' })}
          onError={(e) => { e.target.style.display = 'none'; }}
        />
        {showCounter && (
          <div
            style={{
              position: 'absolute',
              left: '50%',
              bottom: large && isCompact ? 18 : 16,
              transform: 'translateX(-50%)',
              minWidth: 74,
              height: 34,
              padding: '0 14px',
              borderRadius: 999,
              background: 'rgba(0,0,0,0.62)',
              border: '1px solid rgba(255,255,255,0.18)',
              color: '#fff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontFamily: 'Pretendard, sans-serif',
              fontSize: 14,
              fontWeight: 600,
              letterSpacing: 0,
              zIndex: 1,
            }}
          >
            {currentIndex} / {totalCount}
          </div>
        )}
      </div>
    </div>,
    document.body
  );
}
